import { useAppDispatch } from 'app/hooks'
import { CountType } from 'enums'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useEffect } from 'react'
import { Dashboard } from 'schemas/DashboardSchema'
import { createSideBySide } from 'utils/runSearchUtil'
import { toFilter, deriveCountType } from 'utils/widgetUtil'
import { getMeasureByCountTypeAndAnalysisType } from 'utils/measureUtil'
import { getInsightCategory, getEmojiCategory } from 'utils/viewOptionUtil'
import { WidgetId } from 'features/types'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

import { SideBySideWidgetsType } from '../../types'
import { servicePropertySetter } from './utils'

const createSearches = (widgetSetting: SideBySideWidgetsType, dashboardData: Dashboard) => {
  const {
    precision,
    viewOption,
    countType,
    analysisType,
    topicInfos,
    showPrimaryTerms
  } = widgetSetting

  const filter = toFilter(widgetSetting)
  const topicIds = topicInfos?.map((topic) => topic.topicId) as number[]
  const { id: dashboardId, name } = dashboardData
  const measure = getMeasureByCountTypeAndAnalysisType(countType!, analysisType!)

  const insightCountOption = {
    groupInfo: true,
    showPrimaryObject: showPrimaryTerms,
    isAuthorBased: deriveCountType(widgetSetting) === CountType.AUTHORS,
    emojiCategory: getEmojiCategory(viewOption)
  }

  const searches = []

  const baseResultSearch = createSideBySide(
    dashboardId!,
    name!,
    precision!,
    topicIds,
    filter,
    measure!,
    getInsightCategory(viewOption!),
    servicePropertySetter.bind(null, widgetSetting),
    insightCountOption
  )

  searches.push(baseResultSearch)

  return searches
}

const RunSearchEffect = ({
  widgetId,
  children
}: {
  widgetId: WidgetId
  children: React.ReactElement
}) => {
  const widgetSetting = useDashboardWidgetSetting<SideBySideWidgetsType>(widgetId)
  const dashboardData = useDashboardData()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const searches = createSearches(widgetSetting, dashboardData!)

    dispatch(
      getWidgetForDashboard({
        searches,
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp
      })
    )
  }, [widgetSetting, dashboardData, dispatch, refreshTimestamp])

  return children
}

export default RunSearchEffect
